// frontend/src/App.js - CON INICIALIZACIÓN DE WEBSOCKET
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Import components and services
import AppRouter from './Router';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { NotificationProvider, GlobalNotifications } from './components/common/GlobalNotifications';
import { CartProvider } from './hooks/useCart';

import arsenalTheme from './styles/theme';
// Reglas de impresion (detalle de vehiculo y resumen de acciones).
import './styles/print.css';
import ErrorBoundary from './components/common/ErrorBoundary';

// Import Alarm System
import AlarmManager from './components/schematics/AlarmManager';
import { useApiCall } from './hooks/useApi';
import { api } from './services/api';

// 🔄 WEBSOCKET: Importar el servicio WebSocket
import websocketService from './services/websocketService';
import authService from './services/authService';

// Import i18n and language hooks
import './i18n/config';
import { useLanguage } from './hooks/useLanguage';

// Tutorial
import { TutorialProvider } from './contexts/TutorialContext';
import TutorialOverlay from './components/tutorial/TutorialOverlay';
import TutorialSimulator from './components/tutorial/TutorialSimulator';
import GlobalDemoModal from './components/demo/GlobalDemoModal';

// Import global styles
import './styles/globals.css';

const AppContent = () => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const { initializeLanguageFromProfile } = useLanguage();

  const [vehicles, setVehicles] = useState([]);
  const [realtimeData, setRealtimeData] = useState({});
  const isSchematicPage = location.pathname.startsWith('/vehicles/schematics');

  // 🔄 WEBSOCKET: Inicializar conexión WebSocket cuando el usuario está autenticado
  useEffect(() => {
    if (isAuthenticated && user) {
      const token = authService.getToken();
      
      if (token) {
        websocketService.connect(token);
      }
    } else {
      websocketService.disconnect();
    }

    // Cleanup: desconectar al desmontar
    return () => {
      // No desconectar aquí porque el componente se puede re-montar
      // La desconexión se maneja cuando isAuthenticated cambia a false
    };
  }, [isAuthenticated, user]);

  // 🔄 WEBSOCKET: Reconectar cuando el token cambie (refresh)
  useEffect(() => {
    const handleTokenRefresh = () => {
      if (isAuthenticated) {
        const newToken = authService.getToken();
        if (newToken && websocketService.getStatus().isConnected) {
          websocketService.disconnect();
          setTimeout(() => {
            websocketService.connect(newToken);
          }, 500);
        }
      }
    };

    // Escuchar eventos de refresh de token si los tienes
    window.addEventListener('token-refreshed', handleTokenRefresh);
    
    return () => {
      window.removeEventListener('token-refreshed', handleTokenRefresh);
    };
  }, [isAuthenticated]);

  // Load vehicles for alarm context
  const {
    data: vehiclesData
  } = useApiCall(() => isAuthenticated ? api.vehicles.getAll() : null, [isAuthenticated]);

  // Load dashboard data for real-time context
  const {
    data: dashboardData
  } = useApiCall(() => isAuthenticated ? api.telemetry.getDashboard() : null, [isAuthenticated]);

  // Load user profile for language initialization
  const {
    data: userProfile,
    loading: profileLoading
  } = useApiCall(() => isAuthenticated ? api.users.getProfile() : null, [isAuthenticated]);

  // Initialize language from user profile
  useEffect(() => {
    if (userProfile && !profileLoading) {
      initializeLanguageFromProfile(userProfile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, profileLoading]);

  // Update vehicles when data loads
  useEffect(() => {
    if (vehiclesData) {
      setVehicles(vehiclesData);
    }
  }, [vehiclesData]);

  // Process dashboard data for real-time telemetry
  useEffect(() => {
    if (dashboardData?.vehicle_status) {
      const processedData = {};

      dashboardData.vehicle_status.forEach(vehicleStatus => {
        if (vehicleStatus.latest_data) {
          processedData[vehicleStatus.id] = vehicleStatus.latest_data;
        }
      });

      setRealtimeData(processedData);
    }
  }, [dashboardData]);

  // Handle alarm state changes
  const handleAlarmStateChange = (alarmState) => {
    // Log alarm state changes for debugging
    // console.log(`🚨 ${t('app.alarmStateChanged')}`, alarmState);
  };

  return (
    <div className="App">
      {/* Main Application Router */}
      <AppRouter />

      {/* Global Alarm System - Only for authenticated users */}
      {isAuthenticated && user && !isSchematicPage && (
        <AlarmManager
          realtimeData={realtimeData}
          vehicles={vehicles}
          onAlarmStateChange={handleAlarmStateChange}
        />
      )}

      {/* Global Notifications */}
      <GlobalNotifications />
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={arsenalTheme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <AuthProvider>
            <NotificationProvider>
              <Router>
                <TutorialProvider>
                  <CartProvider>
                    <AppContent />
                    <TutorialOverlay />
                    <TutorialSimulator />
                    <GlobalDemoModal />
                  </CartProvider>
                </TutorialProvider>
              </Router>
            </NotificationProvider>
          </AuthProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;